import React from 'react';
import { Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/joy';
import { Computer, Logout, PhoneAndroid, PhoneIphone } from '@mui/icons-material';
import PermissionWrapper from '../PermissionWrapper';
import ConfirmActionButton from '../ConfirmActionButton';

const platformIcon = (platform) => {
    switch ((platform || '').toLowerCase()) {
        case 'android':
            return <PhoneAndroid />;
        case 'ios':
            return <PhoneIphone />;
        default:
            return <Computer />;
    }
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const Row = ({ label, children, mono = false }) => (
    <Typography level="body-xs">
        <Typography textColor="neutral.500">{label}: </Typography>
        <Typography fontFamily={mono ? 'monospace' : undefined}>{children}</Typography>
    </Typography>
);

// One installation (app on a device) of a client: device, app, language,
// masked push token and session state. Admins with admin:write can sign it
// out (onTerminate).
const ClientAppCard = ({ app, onTerminate }) => {
    const appVersion = [app.app_version, app.app_version_code ? `(${app.app_version_code})` : null]
        .filter(Boolean)
        .join(' ');
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {platformIcon(app.platform)}
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-md" noWrap>{app.device_name || app.platform}</Typography>
                        <Typography level="body-sm" textColor="neutral.500" noWrap>
                            {[app.platform, app.os_version].filter(Boolean).join(' · ')}
                        </Typography>
                    </Stack>
                    <Chip size="sm" variant="soft" color={app.signed_in ? 'success' : 'neutral'}>
                        {app.signed_in ? 'Signed in' : 'Signed out'}
                    </Chip>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Row label="App version">{appVersion || '—'}</Row>
                <Row label="Vendor ID" mono>{app.vendor_id}</Row>
                {app.installation_id && <Row label="Installation" mono>{app.installation_id}</Row>}
                {app.user_agent && !app.device_model && <Row label="Browser">{app.user_agent}</Row>}
                <Row label="Language">{app.locale || '—'}</Row>
                <Row label="Push token" mono>
                    {app.push_token_preview
                        ? `${app.push_token_preview}${app.push_provider ? ` (${app.push_provider})` : ''}`
                        : 'none'}
                </Row>
                <Row label="Added">{formatDate(app.created_at)}</Row>
                <Row label="Logs synced">{formatDate(app.logs_synced_at)}</Row>
                {app.signed_in && onTerminate && (
                    <PermissionWrapper requiredPermission="admin:write">
                        <Divider sx={{ my: 1 }} />
                        <ConfirmActionButton
                            label="Terminate session"
                            startDecorator={<Logout />}
                            title={`Sign out ${app.device_name || 'this device'}?`}
                            message="The device will have to sign in again and stops receiving push notifications."
                            confirmLabel="Sign out"
                            onConfirm={onTerminate}
                        />
                    </PermissionWrapper>
                )}
            </CardContent>
        </Card>
    );
};

export default ClientAppCard;
